import * as d3 from 'd3';
import React, { useState } from 'react';
import styled from 'styled-components';
import useResizeObserver from 'use-resize-observer';

import { SvgContext } from './hooks/useCommonContext';

const StyledSVG = styled.svg`
  overflow: visible;
  text {
    font-size: 12px;
    font-family: ${props => props.theme.font.primary};
  }
`;

const Container = styled.div`
  width: 100%;
  height: 200px;
`;

const chartAttributes = {
  margin: {
    top: 20,
    right: 20,
    left: 30,
    bottom: 50,
  },
};

const getScale = (key, keyType = 'number', data, customDomain) => {
  // need to add more conditionals to abstract this component
  switch (keyType) {
    case 'time':
      return d3
        .scaleTime()
        .domain(customDomain || d3.extent(data.map(d => d[key])));
    case 'number':
      return d3
        .scaleLinear()
        .domain(customDomain || d3.extent(data.map(d => d[key])));
    default:
      return d3
        .scaleLinear()
        .domain(customDomain || d3.extent(data.map(d => d[key])));
  }
};

export default function Component({
  children,
  data,
  domChildren,
  keys,
  keyTypes,
}) {
  // this component passes down important data to be used across chart items,
  // including width, height, xScaling, yScaling, data, etc
  const { margin } = chartAttributes;
  const [tooltip, setTooltip] = useState({});
  const [ref, width, height] = useResizeObserver();

  const x = getScale(keys.x, keyTypes.x, data);
  x.rangeRound([margin.left, width - margin.right]);

  const y = getScale(keys.y, keyTypes.y, data);
  y.rangeRound([height - margin.bottom, margin.top]);

  const handleHorizontalTooltip = svg => {
    d3.select(svg).on(
      'touchend mouseleave',
      function () {
        setTooltip({});
      },
      { passive: true }
    );
    d3.select(svg).on(
      'touchmove mousemove',
      function () {
        const bisect = d3.bisector(d => d[keys.x]).left;
        const [xVal, _yVal] = d3.mouse(this); // eslint-disable-line
        const xIndex = bisect(data, x.invert(xVal), 1);
        const hoverDatum = data[xIndex];
        if (hoverDatum) {
          const xValue = hoverDatum[keys.x];
          const xPos = x(xValue);
          const yValue = hoverDatum[keys.y];
          const yPos = y(yValue);
          const customTooltipData = hoverDatum.customTooltipData;
          const tooltip = {
            hoverDatum,
            xValue,
            xPos,
            yValue,
            yPos,
            customTooltipData,
          };
          setTooltip(tooltip);
        }
      },
      { passive: true }
    );
  };

  return (
    <Container ref={ref}>
      <SvgContext.Provider
        value={{
          width,
          height,
          x,
          y,
          data,
          keys,
          tooltip,
          ...chartAttributes,
        }}
      >
        {domChildren}
        <StyledSVG width={'100%'} height={200} ref={handleHorizontalTooltip}>
          {children}
        </StyledSVG>
      </SvgContext.Provider>
    </Container>
  );
}

Component.defaultProps = {
  data: [],
  domChildren: <div />,
  keys: { x: 'x', y: 'y' },
  keyTypes: { x: 'number', y: 'number' },
};
